import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, Lock, Shield, ArrowLeft } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { useAdminAuth } from "../../lib/adminAuth";
import { toast } from "sonner";

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || "/admin";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(email.trim(), password);
      toast.success("Connexion réussie");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Connexion impossible";
      setError(typeof detail === "string" ? detail : "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-10 sm:px-8"
    >
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-cyan-400 hover:text-cyan-300"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour au site
      </Link>

      <div className="surface-card rounded-2xl p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-700/5 ring-1 ring-cyan-500/30">
            <Shield className="h-5 w-5 text-cyan-300" />
          </div>
          <div>
            <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">
              Espace administrateur
            </span>
            <h1 className="mt-0.5 font-heading text-xl font-bold text-slate-50">
              Connexion sécurisée
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <Label htmlFor="admin-email" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-slate-400">
              E-mail
            </Label>
            <Input
              id="admin-email"
              data-testid="admin-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              placeholder="olivier3835@gmail.com"
              className="border-slate-700 bg-slate-900/60 text-slate-100 placeholder:text-slate-500 focus-visible:ring-cyan-500"
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-slate-400">
              Mot de passe
            </Label>
            <Input
              id="admin-password"
              data-testid="admin-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="border-slate-700 bg-slate-900/60 text-slate-100 placeholder:text-slate-500 focus-visible:ring-cyan-500"
            />
          </div>
          {error && (
            <div
              data-testid="admin-login-error"
              className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300"
            >
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-primary-glow w-full gap-2 bg-cyan-500 font-semibold text-slate-950 hover:bg-cyan-400"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Connexion...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" />
                Se connecter
              </>
            )}
          </Button>
        </form>

        <p className="mt-6 text-xs text-slate-500">
          Jeton valide 24 h. Protection anti-brute-force : 5 tentatives puis
          verrouillage 15 min.
        </p>
      </div>
    </div>
  );
}
